import { CustomButton } from "components/Button";
import SlideInLayout, { SlideInHeader } from "components/layouts/SlideInLayout";
import { SlideInFooter } from "components/layouts/SlideInLayout";
import Loader from "components/Spinner";
import { HeadingText, } from "components/TextTranslationContainer";
import { useEventTracker } from "hooks";
import useCurrentUser from "hooks/useCurrentUser";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DiscountAction, NotPersistAction } from "store/actions";
import { UTILS } from "utils";
import { VoucherIcon } from "./discount-icons";
import { TranslationText } from "components/TextTranslationContainer";
import { calculateCartGrossAmount } from "utils/Utils";
import { CartAction } from "store/actions";
import { getLanguage } from "utils/Utils";
import { BrandAction } from "store/actions";
import CONSTANTS from "Constants";
import ErrorComponent from "components/ErrorComponent";
import { t } from "i18next";
import { PriceRender } from "components/DisplayPrice";
import { errorNotification } from "utils/Utils";

const VoucherCard = ({
  discount,
  isSelected=false,
  loading = false,
  disabled = false,
  itemTotal = 0,
  onApply = () => { },
}) => {
  if (!discount) return null;

  const {
    discountAmount,
    discountType,
    minOrderValue,
    perCustomerUse,
  } = discount;
  const isPercentage = discountType === "percentage";

  // Min spend
  const minSpend =
    minOrderValue > 0;

  // Expiry (mocked – in production this would be formatted from real expiry date)
  const expiry = "Use by April 20, 2025";

  const isDisabled = itemTotal < minOrderValue || disabled;

  return (
    <div className="border border-gray-200 rounded-16x p-4 flex flex-col bg-white shadow-sm h-full">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="bg-orange-500 rounded-lg p-2 flex items-center justify-center">
          <VoucherIcon className="primary-svg-path w-6 h-6" />
        </div>
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1">
            <TranslationText
              className="text-12x text-shadow-forge-700"
              text={isPercentage ? `Enjoy ` : `Get `}
            />
            {isPercentage ? (
              <TranslationText
                className="text-12x text-brand-base-primary font-semibold"
                text={`${discountAmount}%`}
              />
            ) : (
              <PriceRender
                className="text-12x text-brand-base-primary leading-none font-semibold"
                price={discountAmount}
              />
            )}
            <TranslationText
              className="text-12x text-shadow-forge-700"
              text={`off your order`}
            />
          </div>
          {perCustomerUse > 0 && (
            <TranslationText
              className="text-12x text-shadow-forge-700"
              text={
                perCustomerUse > 1
                  ? `Applicable times per user`
                  : "Applicable time per user"
              }
              contentProps={{
                perCustomerUse: perCustomerUse,
              }}
            />
          )}
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-2 border-dashed border-gray-200 my-2" />

      {/* Min spend + expiry info */}
      <div className="flex items-center justify-between">
        {minSpend && (
          <div className="flex gap-2 flex-wrap bg-gray-100 rounded-full px-3 py-1 items-center text-xs text-gray-700">
            <TranslationText text={"Min. Spend"} />
            <PriceRender
              className="text-brand-base-primary font-semibold"
              price={minOrderValue}
            />
          </div>
        )}

        <div className="mt-auto">
          <button
            className="text-brand-base-primary font-semibold text-base hover:underline focus:outline-none relative group"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (isDisabled) return;
              onApply();
            }}
            disabled={loading || isDisabled}
          >
            {t(isSelected ? "Applied" : "Use")}

            {isDisabled && (
              <div className="hidden group-hover:flex flex-col items-end absolute -top-10 right-0 z-10 pointer-events-none">
                {/* Tooltip box */}
                <div className="bg-red-500 text-white text-xs px-2 py-1 rounded shadow-md whitespace-nowrap">
                  {t("Minimum order value not met")}
                </div>
                {/* Caret */}
                <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-red-500"></div>
              </div>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};


const ApplyVoucher = ({
  isOpen,
  onClose = () => { },
}) => {

  const { currentUser } = useCurrentUser();
  const dispatch = useDispatch();

  const [voucherCode, setVoucherCode] = useState("");

  const { cart,itemsGrossAmount } = useSelector((state) => state?.cart);
  const { discounts, isLoadingDiscounts } = useSelector(
    (state) => state?.brand
  );

  const { selectedStore } = useSelector((state) => state?.store);
  const { orderType } = useSelector((state) => state?.AppReducer);
  const { brandInfo } = useSelector((state) => state?.brandStorageReducer);
  const { error, discountInfo, discountLoader } = useSelector(
    (state) => state?.Discounts
  );

  useEventTracker({ name: "APPLY_COUPON", orderType, shouldLog: true });

  const filteredDiscounts = UTILS.getFilteredCoupons(
    discounts,
    selectedStore,
    orderType,
    true
  );


  const closeModal = () => {
    if (discountLoader || isLoadingDiscounts) return;

    dispatch(NotPersistAction.toggleDiscountModal(false));
    dispatch(DiscountAction.validateDiscountFailure(null));
    dispatch(
      NotPersistAction.setapplicableItemsDiscountListModal({
        isOpen: false,
        data: [],
      })
    );
    setVoucherCode(null);
    onClose()
  };

  const updateCartStatus = () => {
    const { itemsNetPrice, itemsGrossAmount, orderGrossAmount } =
      calculateCartGrossAmount(cart, discountInfo);
    CartAction.updateCartPrice({
      originalPrice: itemsNetPrice,
      amountAfterDiscount: itemsGrossAmount,
      itemsGrossAmount,
      orderGrossAmount,
    });
  };

  const applyCoupon = (e) => {
    e.preventDefault();
    if (discounts && voucherCode) {
      let convertedCode = voucherCode?.toLowerCase()?.trim();
      const discount = UTILS.getDiscontInfo(discounts, convertedCode);
      if(discount?.discountAppliedOnSpecificItems?.length > 0 && discount?.discountApplication?.toLowerCase() !== "hidden"){
        // updateCartStatus();
        errorNotification(t("It seems like this coupon is either auto applied or available for selected items"))
        return
      }
      else if (discount && selectedStore && currentUser && orderType?.serviceType) {
        const payload = {
          brandId: discount.brandId,
          discountId: discount._id,
          language: getLanguage(),
          storeId: selectedStore._id,
          userId: currentUser.user.userId,
          serviceType: orderType?.serviceType,
        };
        const obj = {
          payload,
          cart,
          discount,
          selectedStore,
        };
        dispatch(DiscountAction.validateDiscount(obj));
        // dispatch(CartAction.removeDiscountAmountFromCartItems());
      }
      // if discount is null
      else if (discount === null) {
        updateCartStatus();
        errorNotification(t(CONSTANTS.COUPON_NOT_FOUND))
        dispatch(
          DiscountAction.validateDiscountFailure(t(CONSTANTS.COUPON_NOT_FOUND))
        );
        dispatch(DiscountAction.setCoponValidation(false));
        return
          
      }
      closeModal();
    }
  };

  useEffect(() => {
    if (!brandInfo?.brand?._id) return;
    dispatch(
      BrandAction.getDiscounts({
        brandId: brandInfo?.brand?._id,
        page: CONSTANTS.COUPONS_POPUP,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandInfo?.brand?._id]);

  useEffect(()=>{
    if(discountInfo?.discountCode){
      setVoucherCode(discountInfo?.discountCode)
    }else{
      setVoucherCode(null)
    }
    return () => {
      setVoucherCode(null)
    }
  },[discountInfo, isOpen])

  return (
  <SlideInLayout open={isOpen} onClose={() => closeModal()} hideOutsideCloseButton={true}
    container={{ className: "slide-in-coupun-card-popup" }}
  >
    {discountLoader || isLoadingDiscounts &&
      <div className="h-full w-full left-0 bottom-0 absolute z-10"
        style={{
          backgroundColor: "rgba(0, 0, 0, 0.1)"
        }}
      >
        <Loader />
      </div>
    }
    <SlideInHeader title="Apply Voucher" onClose={() => closeModal()} />
   
   <div className="apply-coupon-card-popup">
      <div className="flex justify-between w-full flex-col p-6 gap-4">
        <HeadingText text="Enter a voucher code" />
        <div className="mb-6">
          <input
            type="text"
            value={voucherCode}
            onChange={(e) => setVoucherCode(e.target.value)}
            placeholder={t("Enter voucher code")}
            className="w-full border border-gray-300 rounded-16x p-4 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-orange-200"
          />
        </div>
        <HeadingText text="Vouchers" />
        {error && <ErrorComponent error={error} />}
        {
          filteredDiscounts?.length > 0 ?
          filteredDiscounts?.map((discount, index) =>
              <VoucherCard
              key={discount?.id}
              discount={discount}
              isSelected={discount?.discountCode === voucherCode}
              loading={discountLoader || isLoadingDiscounts}
              disabled={discountLoader || isLoadingDiscounts}
              itemTotal={itemsGrossAmount}
              onApply={() => {
                setVoucherCode(discount?.discountCode);
                // applyCoupon(null, discount?.discountCode);
              }}
           />
          )
          :  <div className="flex justify-center items-center h-full">
          <TranslationText className="text-16x text-red-500" text="No vouchers found" />
        </div>
        }
      </div>
      <br />
      <br />
      <br />
      <br />
    </div>
 
    <SlideInFooter>
      <CustomButton
        disabled={discountLoader || isLoadingDiscounts || !voucherCode}
        onClick={applyCoupon}
      >
        {t("Apply Voucher")}
      </CustomButton>
    </SlideInFooter>
  </SlideInLayout>);
};

export default ApplyVoucher;
